/**
 * Browser-storage side of auth: post-login redirect path, pre-selected login-modal
 * role, and the localStorage `user` profile. Uses localStorage (not the Zustand store)
 * for the redirect path since the store may not have persisted/rehydrated yet.
 */

import { USER_DATA_UPDATED_EVENT } from '@/lib/constants/auth-events';
import { getSafePath } from '@/helpers/safe-redirect';
import { ACADEMY_BASE_PATH } from '@/lib/schema/paths';

/** basePath without a trailing slash. lib/schema/paths.ts owns the env read. */
const BASE_PATH = ACADEMY_BASE_PATH.endsWith('/') ? ACADEMY_BASE_PATH.slice(0, -1) : ACADEMY_BASE_PATH;

const PREVIOUS_PATH_STORAGE_KEY = 'academy-previous-path';
const LOGIN_MODAL_ROLE_KEY = 'academy-login-modal-role';

/** Set role to pre-select when opening login modal (e.g. from partner-only overlay). Cleared after read. */
export function setLoginModalRole(role: 'partner' | 'member'): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(LOGIN_MODAL_ROLE_KEY, role);
  } catch {
    // ignore
  }
}

/** Read-once counterpart to setLoginModalRole — clears the value so a later modal opens neutral. */
export function getAndClearLoginModalRole(): 'partner' | 'member' | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(LOGIN_MODAL_ROLE_KEY);
    sessionStorage.removeItem(LOGIN_MODAL_ROLE_KEY);
    if (v === 'partner' || v === 'member') return v;
  } catch {
    // ignore
  }
  return null;
}

/** Strip the basePath prefix, so what gets stored is app-relative. */
function normalizePath(path: string): string {
  if (!path) return path;
  
  if (path.startsWith(BASE_PATH)) {
    const normalized = path.substring(BASE_PATH.length);
    return normalized.startsWith('/') ? normalized : '/' + normalized;
  }
  
  return path.startsWith('/') ? path : '/' + path;
}

/** Pages fine to remember but not to land on right after login — they read progress
 *  written by the login itself, so resuming into them races the fresh session. Opt in
 *  via `excludeProgressPages`. */
const PROGRESS_PAGE_SEGMENTS = ['/assessment'];

/**
 * Whether a path is worth remembering as a post-login destination. The single predicate
 * for this (replacing seven hand-rolled copies that had drifted apart) — keep new rules
 * here, not inline at a call site.
 */
function isValidPathForStorage(
  path: string,
  options?: { excludeProgressPages?: boolean }
): boolean {
  if (!path) return false;
  // Home page: default landing spot anyway. `/?` also catches query variants like
  // /?openLogin=1, which would reopen the login modal right after login.
  if (path === '/' || path === '/academy' || path.startsWith('/?')) return false;
  // Login page is retired, but older stored keys can still hold it; startsWith catches
  // query strings and the basePath-prefixed form too.
  if (path.startsWith('/login') || path.startsWith('/academy/login')) return false;
  if (options?.excludeProgressPages && PROGRESS_PAGE_SEGMENTS.some((s) => path.includes(s))) {
    return false;
  }
  return true;
}

/**
 * Current browser path for the Academy API's SAML `returnTo`, or null when not worth
 * returning to (caller then omits the param and the API falls back to app root).
 * Deliberately keeps the basePath prefix (unlike normalizePath) — the API resolves
 * returnTo against its own appUrl, so a stripped path would redirect outside /academy.
 */
export function getReturnToPath(): string | null {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname + window.location.search;
  return isValidPathForStorage(path, { excludeProgressPages: true }) ? path : null;
}

/** Strip the transient `resume=true` flag and any query separators it leaves dangling. */
function cleanRedirectPath(path: string): string {
  return path.replace('resume=true', '').replace('?&', '?').replace(/[?&]$/, '');
}

/** Turn a stored (basePath-less) path into an absolute same-origin redirect URL —
 *  the open-redirect guard for stored paths; anything off-site falls back to app root. */
export function buildAbsoluteRedirectUrl(normalizedPath: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Never redirect to absolute or protocol-relative URLs (prevent open redirect)
  if (normalizedPath.startsWith('http') || normalizedPath.startsWith('//')) {
    return `${origin}${BASE_PATH}`;
  }

  if (normalizedPath === '/') {
    return `${origin}${BASE_PATH}`;
  }

  const cleanPath = normalizedPath.startsWith('/') ? normalizedPath : '/' + normalizedPath;
  return `${origin}${BASE_PATH}${cleanPath}`;
}

/** Store the previous path in localStorage (survives Zustand store clears), normalized
 *  to drop the basePath. */
export function storePreviousPath(path: string): void {
  if (typeof window === 'undefined' || !path) return;
  
  const normalizedPath = normalizePath(path);

  if (isValidPathForStorage(normalizedPath)) {
    try {
      localStorage.setItem(PREVIOUS_PATH_STORAGE_KEY, normalizedPath);
    } catch (error) {
      // Silently fail
    }
  }
}

/** The stored path, re-validated on read — older builds may have persisted keys that
 *  predate current isValidPathForStorage rules. */
function getStoredPreviousPath(): string | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(PREVIOUS_PATH_STORAGE_KEY);
    if (stored && isValidPathForStorage(stored)) {
      return stored;
    }
  } catch (error) {
    // Silently fail
  }
  return null;
}

/** Drop the stored path. Resolving it consumes it, so this runs from resolvePostLoginUrl. */
function clearStoredPreviousPath(): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(PREVIOUS_PATH_STORAGE_KEY);
  } catch (error) {
    // Silently fail
  }
}

const USER_STORAGE_KEY = 'user';

/**
 * Some Academy API responses use snake_case name/picture fields while the rest of the app
 * reads camelCase — fills in the camelCase field only when missing, so an account with a
 * snake_case login response doesn't fall back to initials/no-picture until a later refetch.
 */
export function normalizeUserFields<T extends Record<string, unknown>>(userData: T): T {
  const normalized: Record<string, unknown> = { ...userData };
  if (normalized.firstName == null && normalized.first_name != null) {
    normalized.firstName = normalized.first_name;
  }
  if (normalized.lastName == null && normalized.last_name != null) {
    normalized.lastName = normalized.last_name;
  }
  if (normalized.profilePicture == null && normalized.profile_picture != null) {
    normalized.profilePicture = normalized.profile_picture;
  }
  return normalized as T;
}

/**
 * Set authenticated user in localStorage and notify listeners (userDataUpdated); use after
 * any auth flow completes so UI/nav stay in sync. The session cookie itself is set by the
 * API response — this only mirrors it into client-visible state.
 *
 * @returns true if stored successfully; false if not in browser or storage failed
 */
export function setAuthUserInStorage(userData: Record<string, unknown>): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const normalized = normalizeUserFields(userData);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent(USER_DATA_UPDATED_EVENT, { detail: normalized }));
    return true;
  } catch {
    // localStorage full, disabled, or private mode restrictions
    return false;
  }
}

/**
 * Extract user object from auth API response body.
 * Handles various shapes: { data }, { user }, { data: { user } }, top-level, and id/uid/email variants.
 * Returns the object to store as localStorage 'user', or null if not found.
 */
export function extractUserFromAuthResponse(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;

  const hasUserLike = (o: unknown): boolean => {
    if (!o || typeof o !== 'object') return false;
    const obj = o as Record<string, unknown>;
    return (
      'uid' in obj ||
      'id' in obj ||
      '_id' in obj ||
      'user_id' in obj ||
      'userId' in obj ||
      'email' in obj ||
      'emailAddress' in obj ||
      'email_address' in obj
    );
  };

  // Most-specific wrapper first, bare body last, so an envelope with stray top-level
  // id fields still resolves to the real user object.
  const dataObj = typeof b.data === 'object' && b.data !== null ? (b.data as Record<string, unknown>) : null;
  const candidates = [
    dataObj?.user,
    dataObj,
    b.user,
    b.userData,
    b.profile,
    b,
  ].filter(Boolean);

  for (const c of candidates) {
    if (typeof c === 'object' && c !== null && hasUserLike(c)) {
      return normalizeUserFields(c as Record<string, unknown>);
    }
  }
  return null;
}

/**
 * The post-login destination resolver: reads the stored previous path, applies every guard
 * once, and returns an absolute same-origin URL, or null if there's no usable destination.
 * Consolidates four guards that used to live as different subsets across three call
 * sites (how the getSafePath allowlist gap went unnoticed) — getSafePath is the route
 * allowlist, buildAbsoluteRedirectUrl the open-redirect guard, the rest hygiene.
 * Resolving CONSUMES the stored path. `excludeProgressPages` stays a caller choice since
 * SSO and local login genuinely disagree on resuming into /assessment.
 */
export function resolvePostLoginUrl(options?: { excludeProgressPages?: boolean }): string | null {
  const stored = getStoredPreviousPath();
  clearStoredPreviousPath();
  if (!stored) return null;

  // Re-validate with the caller's rules — getStoredPreviousPath() only checked the baseline ones.
  if (!isValidPathForStorage(stored, options)) return null;

  // Re-check after stripping query cruft: removing `resume=true` could leave just '/'.
  const cleaned = cleanRedirectPath(stored);
  if (!isValidPathForStorage(cleaned)) return null;

  return buildAbsoluteRedirectUrl(getSafePath(cleaned, '/'));
}

